import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, filter, map, mergeMap, of, switchMap, tap } from 'rxjs';

import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import {
  locationCodesClearedMessage,
  LOCATION_UPLOAD_MESSAGES,
  locationUploadRejectedMessage,
  locationUploadSuccessMessage,
  QUALIFICATION_CODE,
  SURVEY_ACTIVITY_LOG_MESSAGES,
  SurveyLocationUploadResult,
} from '@ps/survey-utils';
import {
  UserSettingsActionsFacade,
  UserSettingsSelectorsFacade,
} from '@ps/user-settings-data-access';
import {
  SupplierApiActions,
  SurveyActivityLogActions,
} from '@ps/survey-data-access';
import { TagsActionsFacade } from '@ps/tags-data-access';

import { SurveyTagsService } from '../../services/survey-tags/survey-tags.service';
import { LocationUploadService } from '../../services/location/location-upload.service';
import { SurveyProcessingService } from '../../services/survey-processing/survey-processing.service';

import { SurveyCreationCoreApiActions } from '../core/survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationAudienceApiActions,
} from './survey-creation-audience.actions';
import {
  selectLinkedTagIds,
  selectLocationFileUpload,
  selectQualificationsForm,
  selectSelectedTagIds,
} from './survey-creation-audience.selectors';
import { selectSurveyId } from '../core/survey-creation-core.selectors';
import { hasSurveyTagsChanged } from './utils/survey-tags-changes.helper';

@Injectable()
export class SurveyCreationAudienceEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _toastService = inject(ToastService);

  private readonly _surveyProcessingService = inject(SurveyProcessingService);
  private readonly _locationUploadService = inject(LocationUploadService);
  private readonly _surveyTagsService = inject(SurveyTagsService);

  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);
  private readonly _userSettingsActions = inject(UserSettingsActionsFacade);
  private readonly _tagsActions = inject(TagsActionsFacade);

  // Audience-only: pre-draft, create and edit are the only stages that ever
  // show a tag picker — launch has no use for the catalog, so this keys off
  // SurveyCreationAudienceActions.init, not the launch one.
  public readonly loadTagsCatalog$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationAudienceActions.init),
        filter(
          () => !!this._userSettingsSelectors.buyerSettings().enableSurveyTags,
        ),
        tap(() => this._tagsActions.loadTags()),
      ),
    { dispatch: false },
  );

  // A managed-allocations Apply echoes the applied blend on its success action;
  // mirror it into the survey-creation state so the picker stays in sync. The
  // non-managed cpi-drawer Apply omits `blendId`, so it never touches state.
  public readonly syncSuppliersMetaDataBlendId$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierApiActions.updateAllocationsSuccess),
      filter((action) => 'blendId' in action),
      map((action) =>
        SurveyCreationAudienceActions.setSuppliersMetaDataBlendId({
          blendId: action.blendId ?? '',
        }),
      ),
    ),
  );

  // The managed Apply persists the global price override, but the PUT response
  // never echoes it back — so mirror the applied pricing (carried on the success
  // action) into suppliersMetaData, the single source the panel reads to pick its
  // CPI mode. Non-managed Apply omits these keys, so it never touches state.
  public readonly syncSuppliersMetaDataPricing$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierApiActions.updateAllocationsSuccess),
      filter((action) => 'priceOverride' in action),
      map((action) =>
        SurveyCreationAudienceActions.setSuppliersMetaData({
          metaData: {
            priceOverride: action.priceOverride ?? null,
            manualOverride: action.manualOverride ?? false,
            surveyPauseThreshold: action.surveyPauseThreshold ?? null,
            defaultPurePriceOverride: action.defaultPurePriceOverride ?? false,
            isPurePriceRateCardEnable:
              action.isPurePriceRateCardEnable ?? false,
            supplierDefaultAllocation:
              action.supplierDefaultAllocation ?? false,
          },
        }),
      ),
    ),
  );

  // Saving a blend creates it with a server id; mirror that id into the survey-
  // creation state so the picker selects the freshly saved blend from the single
  // metadata source rather than a separate saved-id slice.
  public readonly syncSavedBlendId$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierApiActions.saveBlendSuccess),
      map(({ blend }) =>
        SurveyCreationAudienceActions.setSuppliersMetaDataBlendId({
          blendId: blend.id,
        }),
      ),
    ),
  );

  public readonly updatedSurveyQualificationsValidation$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationAudienceActions.updateQualifications),
        concatLatestFrom(() => [
          this._store.select(selectSurveyId),
          this._store.select(selectQualificationsForm),
        ]),
        filter(([, surveyId]) => !!surveyId),
        tap(([, , qualifications]) => {
          Option.from(qualifications.errors[0]?.message).inspect((message) =>
            this._toastService.error(message, { keepOpen: false }),
          );
        }),
      ),
    { dispatch: false },
  );

  public readonly uploadLocationCodes$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.uploadLocationCodes),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      mergeMap(
        ([
          { countryCode, qualificationCode, qualificationName, file },
          surveyId,
        ]) =>
          this._locationUploadService
            .uploadLocationCodes(
              countryCode,
              surveyId ?? 0,
              qualificationCode,
              file,
            )
            .pipe(
              tap((result) =>
                this._notifyLocationUpload(
                  result,
                  qualificationCode,
                  qualificationName,
                ),
              ),
              map((result) =>
                SurveyCreationAudienceApiActions.uploadLocationCodesSuccess({
                  qualificationCode,
                  qualificationName,
                  result,
                }),
              ),
              this.psCatchError(
                (error) =>
                  of(
                    SurveyCreationAudienceApiActions.uploadLocationCodesFailure(
                      {
                        qualificationCode,
                        error:
                          error.message ||
                          LOCATION_UPLOAD_MESSAGES.UPLOAD_FAILED,
                      },
                    ),
                  ),
                { notify: true },
              ),
            ),
      ),
    ),
  );

  public readonly notifyLocationCodesCleared$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationAudienceActions.clearLocationCodes),
        tap(({ qualificationName }) => {
          this._toastService.success(
            locationCodesClearedMessage(qualificationName),
            { keepOpen: false },
          );
        }),
      ),
    { dispatch: false },
  );

  public readonly downloadLocationCodesTemplate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationAudienceActions.downloadLocationCodesTemplate),
        switchMap(({ countryCode, qualificationCode }) =>
          this._locationUploadService
            .downloadTemplateFile(countryCode, qualificationCode)
            .pipe(this.psCatchError(() => EMPTY, { notify: true })),
        ),
      ),
    { dispatch: false },
  );

  public readonly downloadValidatedLocationFile$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationAudienceActions.downloadValidatedLocationFile),
        concatLatestFrom(() => this._store.select(selectLocationFileUpload)),
        map(([{ qualificationCode }, upload]) =>
          upload.qualificationCode === qualificationCode
            ? Option.from(upload.result)
            : Option.none,
        ),
        filter((upload) => upload.isSome()),
        switchMap((upload) => {
          const result = upload.unwrap();

          return this._locationUploadService
            .downloadValidatedFile(result.filePath, result.validCodes)
            .pipe(this.psCatchError(() => EMPTY, { notify: true }));
        }),
      ),
    { dispatch: false },
  );

  public readonly loadSurveyTagsForEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess),
      filter(
        () => !!this._userSettingsSelectors.buyerSettings().enableSurveyTags,
      ),
      switchMap(({ survey }) =>
        this._surveyTagsService.getSurveyTags(survey.id).pipe(
          map((tagIds) =>
            SurveyCreationAudienceApiActions.loadSurveyTagsSuccess({ tagIds }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationAudienceApiActions.loadSurveyTagsFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly linkSurveyTags$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationAudienceApiActions.createStandardSurveySuccess,
        SurveyCreationCoreApiActions.saveSurveySuccess,
      ),
      filter(
        () => !!this._userSettingsSelectors.buyerSettings().enableSurveyTags,
      ),
      concatLatestFrom(() => [
        this._store.select(selectSelectedTagIds),
        this._store.select(selectLinkedTagIds),
      ]),
      filter(([, selectedTagIds, linkedTagIds]) =>
        hasSurveyTagsChanged(selectedTagIds, linkedTagIds),
      ),
      map(([{ survey }, selectedTagIds]) => ({
        surveyId: survey.id,
        selectedTagIds,
      })),
      switchMap(({ surveyId, selectedTagIds }) =>
        this._surveyTagsService.linkSurveyTags(surveyId, selectedTagIds).pipe(
          map((tagIds) =>
            SurveyCreationAudienceApiActions.linkSurveyTagsSuccess({ tagIds }),
          ),
          this.psCatchError(
            (error) =>
              of(
                SurveyCreationAudienceApiActions.linkSurveyTagsFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly toggleManualProcessing$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.toggleManualProcessing),
      filter(() =>
        this._userSettingsSelectors.buyerConfigSettingsId().isSome(),
      ),
      switchMap(({ companyId, enabled }) => {
        const buyerConfigSettingsId = this._userSettingsSelectors
          .buyerConfigSettingsId()
          .unwrap();

        return this._surveyProcessingService
          .updateManualProcessing(buyerConfigSettingsId, companyId, enabled)
          .pipe(
            tap(() => {
              this._toastService.success(
                'Processing setting updated successfully',
              );
            }),
            tap(() => {
              this._userSettingsActions.updateBuyerSetting(
                'enableManualProcessing',
                companyId,
                enabled,
              );
            }),
            switchMap(() => {
              const logAction = SurveyActivityLogActions.logOperatorActivity({
                log: {
                  message: SURVEY_ACTIVITY_LOG_MESSAGES.manualProcessingToggled,
                  metadata: { action: enabled ? 'ON' : 'OFF' },
                },
              });

              return of(logAction);
            }),
            this.psCatchError(() => EMPTY, { notify: true }),
          );
      }),
    ),
  );

  private _notifyLocationUpload(
    result: SurveyLocationUploadResult,
    qualificationCode: number,
    qualificationName: string,
  ): void {
    if (!result.validCodes.length) {
      this._toastService.error(
        qualificationCode === QUALIFICATION_CODE.FSA
          ? LOCATION_UPLOAD_MESSAGES.INVALID_FSA_CODE_FILE
          : LOCATION_UPLOAD_MESSAGES.INVALID_LOCATION_CODE_FILE,
        { keepOpen: false },
      );
      return;
    }

    if (result.incorrectCodeCount) {
      this._toastService.warning(
        locationUploadRejectedMessage(
          result.incorrectCodeCount,
          result.incorrectCodes,
        ),
        { keepOpen: false },
      );
    }

    this._toastService.success(
      locationUploadSuccessMessage({
        correctCodeCount: result.correctCodeCount,
        totalCodeCount: result.totalCodeCount,
        duplicateCodeCount: result.duplicateCodeCount,
        modifiedCodeCount: result.modifiedCodeCount,
        qualificationName,
      }),
      { keepOpen: false },
    );
  }
}
